import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { STATUS_META, statusBg, statusText, statusLabel, formatSqYd } from "@/lib/statusUtils";
import { directionRegion, portionDisplayColor } from "@/lib/portionUtils";
import PlotDetailDialog from "@/components/PlotDetailDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, ZoomIn, ZoomOut, RotateCcw, Filter } from "lucide-react";
import { toast } from "sonner";

export default function LayoutMap() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lps, setLps] = useState([]);
  const [currentLp, setCurrentLp] = useState(null);
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("");
  const [selectedPlot, setSelectedPlot] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lps");
        setLps(data);
        const lpParam = searchParams.get("lp");
        const initial = data.find((l) => l.id === lpParam) || data[0];
        if (initial) setCurrentLp(initial);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentLp) return;
    setSearchParams({ lp: currentLp.id }, { replace: true });
    setLoading(true);
    api
      .get("/plots", { params: { lp_id: currentLp.id } })
      .then((r) => setPlots(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [currentLp, setSearchParams]);

  const filtered = useMemo(() => {
    const size = Number(sizeFilter);
    return plots.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (search && String(p.plot_no).indexOf(search.trim()) === -1) return false;
      if (sizeFilter && Number.isFinite(size) && Math.abs((p.total_sqyd || 0) - size) > 0.01) return false;
      return true;
    });
  }, [plots, search, statusFilter, sizeFilter]);

  // Ids of plots that match the current filters; used to highlight/dim on the grid.
  const matchIds = useMemo(() => new Set(filtered.map((p) => p.id)), [filtered]);
  const hasActiveFilter = search || statusFilter !== "all" || sizeFilter;

  const statusCounts = useMemo(() => {
    const c = {};
    plots.forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [plots]);

  const openPlot = async (plot) => {
    try {
      const { data } = await api.get(`/plots/${plot.id}`);
      setSelectedPlot(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onBooked = () => {
    setSelectedPlot(null);
    // reload plots
    if (currentLp) {
      api.get("/plots", { params: { lp_id: currentLp.id } }).then((r) => setPlots(r.data));
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Interactive Layout Map</div>
          <h1 className="font-heading text-2xl md:text-4xl">Plot Map</h1>
        </div>

        {/* LP Tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="lp-tabs">
          {lps.map((lp) => (
            <button
              key={lp.id}
              data-testid={`lp-tab-${lp.lp_no}`}
              onClick={() => setCurrentLp(lp)}
              className={`px-4 py-2 text-sm border transition-colors ${
                currentLp?.id === lp.id
                  ? "bg-[#234B36] text-white border-[#234B36]"
                  : "bg-white border-[#E5E2DC] hover:bg-[#F0EFEA]"
              }`}
            >
              <span className="font-mono-anu text-xs opacity-70 mr-2">{lp.phase_name}</span>
              {lp.lp_no}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="border border-[#E5E2DC] bg-white p-4 md:p-5 flex flex-wrap gap-3 items-center justify-between mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6653]" />
              <Input
                placeholder="Search plot #"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="plot-search-input"
                className="pl-9 w-48 rounded-sm"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 rounded-sm" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2 text-[#5C6653]" />
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="fully_vacant">Fully Available</SelectItem>
                <SelectItem value="partially_vacant">Partially Available</SelectItem>
                <SelectItem value="booked">Booked</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
                <SelectItem value="hold">Hold</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="mortgage">Mortgage</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Size (sq.yd)"
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              data-testid="map-size-filter"
              className="w-32 rounded-sm"
            />
            {sizeFilter && (
              <button
                onClick={() => setSizeFilter("")}
                data-testid="clear-size-filter"
                className="text-xs px-2 py-1 border border-[#E5E2DC] rounded-sm hover:bg-[#F0EFEA]"
              >Clear size</button>
            )}
          </div>
          <div className="hidden md:flex gap-4 text-xs">
            {Object.entries(STATUS_META).filter(([k]) => k !== "booking_pending").map(([k, m]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="status-dot" style={{ background: m.bg }} />
                <span className="text-[#5C6653]">{m.label}</span>
                <span className="font-mono-anu text-[#1A1F16]">{statusCounts[k] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick size chips */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="map-size-chips">
          {[120, 198, 208.8, 240, 266.6, 300, 330, 360, 480].map((s) => {
            const active = Number(sizeFilter) === s;
            return (
              <button
                key={s}
                onClick={() => setSizeFilter(active ? "" : String(s))}
                data-testid={`map-size-chip-${s}`}
                className={`text-xs px-2.5 py-1 border rounded-full transition-colors ${
                  active ? "bg-[#234B36] text-white border-[#234B36]" : "bg-white text-[#1A1F16] border-[#E5E2DC] hover:bg-[#F0EFEA]"
                }`}
              >
                {s} sq.yd
              </button>
            );
          })}
        </div>

        {/* Reference Layout Image (when LP has one) */}
        {currentLp?.layout_image_url && (
          <div className="border border-[#E5E2DC] bg-white mb-4 overflow-hidden" data-testid="lp-layout-image-panel">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E5E2DC] bg-[#F0EFEA]">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold">Master Layout · Reference</div>
              <div className="font-heading text-sm">{currentLp.phase_name} · {currentLp.lp_no}</div>
            </div>
            <div className="max-h-[420px] overflow-auto bg-[#F9F8F6]">
              <img
                src={currentLp.layout_image_url}
                alt={`${currentLp.lp_no} master layout`}
                className="w-full h-auto"
                data-testid="lp-layout-image"
              />
            </div>
            <div className="px-4 py-2 text-[11px] text-[#5C6653] border-t border-[#E5E2DC]">
              Official approved layout for this LP. Use the interactive grid below to click a plot and view details.
            </div>
          </div>
        )}

        {/* Map */}
        <div className="border border-[#E5E2DC] bg-[#F4F2EC] relative overflow-hidden" style={{ minHeight: "560px" }}>
          {currentLp && (
            <div className="absolute top-3 left-3 z-10 bg-white/95 backdrop-blur border border-[#E5E2DC] px-3 py-2 text-xs">
              <div className="font-mono-anu text-[10px] text-[#5C6653]">{currentLp.phase_name}</div>
              <div className="font-heading text-sm">{currentLp.lp_no}</div>
              <div className="text-[#5C6653] mt-0.5">{filtered.length} / {plots.length} plots</div>
            </div>
          )}

          <TransformWrapper
            initialScale={1}
            minScale={0.4}
            maxScale={4}
            wheel={{ step: 0.1 }}
            doubleClick={{ mode: "reset" }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1">
                  <Button data-testid="zoom-in-btn" onClick={() => zoomIn()} size="icon" variant="outline" className="h-8 w-8 rounded-sm bg-white"><ZoomIn className="h-4 w-4" /></Button>
                  <Button data-testid="zoom-out-btn" onClick={() => zoomOut()} size="icon" variant="outline" className="h-8 w-8 rounded-sm bg-white"><ZoomOut className="h-4 w-4" /></Button>
                  <Button data-testid="reset-zoom-btn" onClick={() => resetTransform()} size="icon" variant="outline" className="h-8 w-8 rounded-sm bg-white"><RotateCcw className="h-4 w-4" /></Button>
                </div>
                <TransformComponent wrapperStyle={{ width: "100%", height: "560px" }} contentStyle={{ width: "100%" }}>
                  <div className="p-8 w-full min-h-[560px]">
                    {loading ? (
                      <div className="text-center text-[#5C6653] py-24">Loading plots…</div>
                    ) : plots.length === 0 ? (
                      <div className="text-center text-[#5C6653] py-24">
                        <div className="font-heading text-lg text-[#1A1F16] mb-2">No plots available for this LP</div>
                        <div className="text-sm">The plot register has not been uploaded yet for {currentLp?.lp_no}.</div>
                      </div>
                    ) : (
                      <PlotGrid plots={plots} matchIds={matchIds} dim={hasActiveFilter} onSelect={openPlot} />
                    )}
                  </div>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-[#5C6653]" data-testid="map-legend">
          <span className="font-semibold uppercase tracking-[0.15em] text-[10px]">Legend</span>
          <LegendSwatch color="#347355" label="Fully Available" />
          <LegendSwatch color="#D97736" label="Partially Available" />
          <LegendSwatch color="#2C5E7A" label="Booked" />
          <LegendSwatch color="#858585" label="Sold" />
          <LegendSwatch color="#DC2626" label="Hold / Blocked" />
          <LegendSwatch color="#4B4B4B" label="Mortgage" />
          <LegendSwatch color="#EAB308" label="Pending SA Approval" dot dark />
          <LegendSwatch color="#F0EFEA" label="Split (see sub-regions)" bordered />
        </div>

        <div className="mt-3 text-xs text-[#5C6653]">
          Tip: Use mouse-wheel or the +/- buttons to zoom. Click and drag to pan. Click a plot to view details.
        </div>
      </div>

      <PlotDetailDialog
        plot={selectedPlot}
        onClose={() => setSelectedPlot(null)}
        onBooked={onBooked}
      />
    </AppShell>
  );
}

function PlotGrid({ plots, matchIds, dim, onSelect }) {
  // Arrange plots into a responsive grid that mimics a layout.
  // For visual balance, we compute columns based on total count.
  const count = plots.length;
  const cols = Math.min(30, Math.max(10, Math.ceil(Math.sqrt(count * 1.6))));
  return (
    <div
      className="grid gap-1"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(28px, 1fr))` }}
      data-testid="plot-grid"
    >
      {plots.map((p) => {
        const isMatch = !dim || (matchIds && matchIds.has(p.id));
        const portions = p.portions || [];
        const isSplit = portions.length > 1;
        const hasPending = !!p.has_pending_portion || portions.some((x) => x.approval_status === "pending_approval");
        const hasRejected = !!p.has_rejected_portion || portions.some((x) => x.approval_status === "rejected");
        // A non-split plot that has been proposed for splitting (edge case:
        // portion_count == 1 but that portion is pending) — we still show the
        // yellow "pending" tint on the whole tile.
        const singlePending = !isSplit && portions[0]?.approval_status === "pending_approval";
        const tileBg = singlePending ? "#EAB308" : statusBg(p.status);
        return (
          <button
            key={p.id}
            onClick={() => onSelect(p)}
            data-testid={`plot-tile-${p.plot_no}`}
            data-match={isMatch ? "1" : "0"}
            data-split={isSplit ? "1" : "0"}
            data-has-pending={hasPending ? "1" : "0"}
            title={`Plot #${p.plot_no} · ${statusLabel(p.status)} · ${formatSqYd(p.total_sqyd)}${isSplit ? ` · ${portions.length} portions` : ""}${hasPending ? " · Pending SA approval" : ""}`}
            className={`plot-tile aspect-square relative flex items-center justify-center text-[10px] font-mono-anu font-medium border transition-all overflow-hidden ${
              isMatch
                ? "border-black/5 shadow-sm"
                : "border-transparent grayscale opacity-25 hover:opacity-60"
            }`}
            style={isSplit
              ? { background: "#F0EFEA", color: "#1A1F16" }
              : { background: tileBg, color: singlePending ? "#1A1F16" : statusText(p.status) }}
          >
            {isSplit && portions.map((portion) => (
              <span
                key={portion.id}
                style={{ ...directionRegion(portion.direction), background: portionDisplayColor(portion) }}
                aria-hidden="true"
              />
            ))}
            {isSplit && (
              <span
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.5)" }}
                aria-hidden="true"
              />
            )}
            {hasPending && (
              <span
                className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-[#EAB308] ring-1 ring-white z-20 pointer-events-none"
                data-testid={`plot-pending-dot-${p.plot_no}`}
                aria-hidden="true"
              />
            )}
            {hasRejected && !hasPending && (
              <span
                className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-[#DC2626] ring-1 ring-white z-20 pointer-events-none"
                aria-hidden="true"
              />
            )}
            <span className="relative z-10" style={{ textShadow: (isSplit || hasPending) ? "0 1px 2px rgba(0,0,0,0.35)" : "none", color: (isSplit || singlePending) ? "#FFFFFF" : statusText(p.status) }}>
              {p.plot_no}
            </span>
          </button>
        );
      })}
    </div>
  );
}


function LegendSwatch({ color, label, dot, dark, bordered }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span
        className="inline-block h-3 w-3 rounded-sm relative"
        style={{
          background: color,
          border: bordered ? "1px solid #D6D3CA" : "none",
        }}
        aria-hidden="true"
      >
        {dot && (
          <span
            className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-[#EAB308] ring-1 ring-white"
            aria-hidden="true"
          />
        )}
      </span>
      <span className={dark ? "text-[#8B6A00] font-medium" : ""}>{label}</span>
    </span>
  );
}
